/**
 * Player flock bodies — issue #187 (split from #173, alongside #185 stances
 * and #186 segmentCount).
 *
 * `bodyCount` (Parasite colony: 3, Modular drone swarm: 5) authors more than
 * one body sharing a run, but only one of them is ever the *controlled* one:
 * `Game.playerIdx` picks it, input drives it through `MoverState`, the camera
 * follows it, and it alone carries `Comp.PLAYER` — exactly like every
 * archetype with `bodyCount: 1`. The rest are plain `Team.PLAYER` entities
 * with their own `Comp.HEALTH`, spawned and stepped by this module.
 *
 * That is a deliberate scope-down from the two control models #187 laid out
 * (see its own writeup): simultaneous N-body input would mean N `MoverState`s,
 * N sets of abilities live at once, and a camera that frames more than one
 * point — none of which exists today, and all of which risks regressing the
 * ~30 single-body archetypes that already work for a feature two archetypes
 * use. A flock body instead falls under real gravity and collides with real
 * terrain (`stepBody`, the same integrator `stepEnemyBodies` runs) and closes
 * on the controlled body on its own — a companion an enemy round can hit and
 * the colony can lose, not a fourth control scheme grafted onto `Game`.
 *
 * `damageModel` ('per-body' vs 'shared-pool') and `respawn-body` revival are
 * *not* this module's concern — both are routing decisions `Game.damageEntity`
 * and `Game.tryRevive` make using the entity indices this module hands them.
 */

import { Comp, Team, type World } from '../entity/ecs';
import type { CellWorld } from '../sim/world';
import { stepBody } from '../entity/body';
import { MOVE } from '../entity/ai/pathing';
import { bodyForArchetype, compositionIds } from '../entity/archetypeBody';
import type { PixelBodySystem } from '../entity/pixelBodySystem';

/**
 * Past this distance a flock body catches up instantly instead of trailing
 * forever behind geometry it has no way to navigate (no jump, no pathing —
 * see the module doc). A body that fell down a shaft the controlled body
 * jumped over would otherwise be stuck there for the rest of the run.
 */
const TELEPORT_LEASH = 700;
/** Inside this radius a flock body stops closing and just stands its ground. */
const FOLLOW_SLACK = 20;

/**
 * Spawn `bodyCount - 1` extra bodies beside `primary`, mirroring its size,
 * composition and max HP so a hit reads the same whichever body it lands on.
 * Returns their entity indices; empty for any `bodyCount <= 1`.
 */
export function spawnFlockExtras(
  w: World,
  bodies: PixelBodySystem,
  primary: number,
  archetypeId: string,
  bodyCount: number,
): number[] {
  const extras: number[] = [];
  if (bodyCount <= 1) return extras;
  const body = bodyForArchetype(archetypeId);
  const comp = compositionIds(body);

  for (let n = 1; n < bodyCount; n++) {
    let e: number;
    try {
      e = w.create(Comp.TRANSFORM | Comp.BODY | Comp.SPRITE | Comp.HEALTH);
    } catch {
      break; // entity table full; the ones already spawned still stand
    }
    const i = e & 0xfffff;
    // Fanned around the primary so a fresh colony does not spawn stacked on
    // one pixel — the same spread `makeSummonHandler` uses for a drone volley.
    const spread = (n - (bodyCount - 1) / 2) * (body.halfW * 2 + 4);
    w.x[i] = w.x[primary] + spread;
    w.y[i] = w.y[primary];
    w.vx[i] = w.vy[i] = 0;
    w.halfW[i] = body.halfW;
    w.halfH[i] = body.halfH;
    w.maxHp[i] = w.maxHp[primary];
    w.hp[i] = w.maxHp[i];
    w.team[i] = Team.PLAYER;
    w.mass[i] = w.mass[primary];
    w.gravityScale[i] = 1;
    w.bodyFlags[i] = 0;
    w.facing[i] = w.facing[primary] || 1;
    w.coreMat[i] = comp.core;
    for (let k = 0; k < 4; k++) {
      w.compMat[i * 4 + k] = comp.mats[k];
      w.compFrac[i * 4 + k] = comp.fracs[k];
    }
    bodies.attach(w, i, body.plan);
    extras.push(i);
  }
  return extras;
}

/**
 * Gravity, terrain collision and a chase toward the controlled body, once per
 * sim tick. A flock body is a body — it falls, lands and catches on ledges
 * the same as an enemy does, rather than floating to its mark.
 */
export function stepPlayerFlock(
  w: World,
  cells: CellWorld,
  primary: number,
  extras: readonly number[],
  dt: number,
): void {
  if (w.alive[primary] !== 1) return;
  for (const i of extras) {
    if (w.alive[i] !== 1 || w.hp[i] <= 0) continue;
    const dx = w.x[primary] - w.x[i];
    const dy = w.y[primary] - w.y[i];
    const dist = Math.hypot(dx, dy);

    if (dist > TELEPORT_LEASH) {
      w.x[i] = w.x[primary];
      w.y[i] = w.y[primary];
      w.vx[i] = w.vy[i] = 0;
      continue;
    }

    if (dist > FOLLOW_SLACK) {
      w.vx[i] = (dx / dist) * MOVE.maxRunSpeed;
      w.facing[i] = dx >= 0 ? 1 : -1;
    } else {
      w.vx[i] = 0;
    }

    const g = (w.vy[i] < 0 ? MOVE.riseGravity : MOVE.fallGravity) * w.gravityScale[i];
    w.vy[i] = Math.min(MOVE.maxFallSpeed, w.vy[i] + g * dt);

    stepBody(w, i, cells, dt);
  }
}
